import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { auctionApi } from 'src/api/auctionApi';
import { Circle, circleApi } from 'src/api/circles';
import {
  AllMessagesResponse,
  CIRCLE_MESSAGES_PAGE,
  CIRCLE_MESSAGES_PAGE_SIZE,
  messagesApi,
} from 'src/api/messagesApi';

import { RootState } from 'src/redux/store/store';

interface CircleDetailState {
  loading: boolean;
  circleData?: Circle;
  videoUrl: string;
  auctionId: string;
  auctionData: any;
  videoMessages: AllMessagesResponse;
  error: string | null;
  memberMessages: AllMessagesResponse;
  loadingMemberMessages: boolean;
  loadingCircleMessages: boolean;
}

const initialState: CircleDetailState = {
  loading: false,
  videoUrl: '',
  auctionId: '',
  auctionData: null,
  memberMessages: {
    result: [],
    replies: [],
    pagination: {
      pageSize: 10,
      totalPages: 0,
      currentPage: 1,
    },
  },
  loadingMemberMessages: false,
  loadingCircleMessages: true,
  videoMessages: {
    result: [],
    replies: [],
    pagination: {
      pageSize: 10,
      totalPages: 0,
      currentPage: 1,
    },
  },
  error: null,
};

export const fetchCircleBase = createAsyncThunk(
  'circleDetail/fetchCircleBase',
  async (id: string) => {
    const circleData = await circleApi.getCircle(id);
    return {
      circleData,
      videoUrl: circleData.welcome_video_id,
    };
  }
);

export const fetchMessagesForMember = createAsyncThunk(
  'circleDetail/fetchMessagesForMember',
  async (_, { rejectWithValue }) => {
    try {
      const res = await messagesApi.getMessagesMember();
      return res;
    } catch (error: any) {
      return rejectWithValue('Failed to fetch member messages');
    }
  }
);

export const fetchAuctionIfLeader = createAsyncThunk(
  'circleDetail/fetchAuctionIfLeader',
  async (id: string, { rejectWithValue }) => {
    try {
      const auctions = await auctionApi.getAuctionList(id);
      const auction = auctions?.[0] ?? null;
      return {
        auctionId: auction?._id ?? '',
        auctionData: auction,
      };
    } catch (error: any) {
      if (error.response?.status === 403) {
        return rejectWithValue('Not authorized to view auction data.');
      }
      throw error;
    }
  }
);

export const fetchCircleMessages = createAsyncThunk(
  'circleDetail/fetchCircleMessages',
  async (
    { circleId, isLeader }: { circleId: string; isLeader: boolean },
    { rejectWithValue }
  ) => {
    try {
      const pageParams = {
        page: CIRCLE_MESSAGES_PAGE,
        pageSize: CIRCLE_MESSAGES_PAGE_SIZE,
      };

      return isLeader
        ? await messagesApi.getMessagesLeader(circleId, pageParams)
        : await messagesApi.getMessagesMember({
            tribe_id: circleId,
            ...pageParams,
          });
    } catch (error: any) {
      if (error.response?.status === 403) {
        return rejectWithValue('Not authorized to view messages.');
      }
      throw error;
    }
  }
);

export const fetchCircleMessagesIfLeader = (circleId: string) =>
  fetchCircleMessages({ circleId, isLeader: true });

export const updateCircle = createAsyncThunk(
  'circleDetail/updateCircle',
  async (
    { id, updates }: { id: string; updates: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      await circleApi.editCircle(updates, id);
      return updates;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Unknown error occurred');
    }
  }
);

export const editUserCount = createAsyncThunk(
  'circleDetail/editUserCount',
  async (
    { id, member_count_max }: { id: string; member_count_max: number },
    { rejectWithValue }
  ) => {
    try {
      await circleApi.editUserCount(id, member_count_max);
      return { id, member_count_max };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to edit user count'
      );
    }
  }
);

const circleDetailSlice = createSlice({
  name: 'circleDetail',
  initialState,
  reducers: {
    clearCircleDetail: (state) => {
      Object.assign(state, initialState);
    },
    addVideoMessage: (state, action) => {
      state.videoMessages.result.unshift(action.payload);
    },
    addReplyMessage: (state, action) => {
      state.memberMessages.replies.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCircleBase.pending, (state) => {
        Object.assign(state, initialState);
        state.loading = true;
      })
      .addCase(fetchCircleBase.fulfilled, (state, action) => {
        state.circleData = action.payload.circleData;
        state.videoUrl = action.payload.videoUrl;
        state.loading = false;
      })
      .addCase(fetchCircleBase.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.loading = false;
        state.error = action.error.message || 'Failed to load circle data';
      })
      .addCase(fetchAuctionIfLeader.fulfilled, (state, action) => {
        state.auctionId = action.payload.auctionId;
        state.auctionData = action.payload.auctionData;
      })
      .addCase(fetchCircleMessages.pending, (state) => {
        state.loadingCircleMessages = true;
      })
      .addCase(fetchCircleMessages.fulfilled, (state, action) => {
        state.videoMessages = action.payload;
        state.loadingCircleMessages = false;
      })
      .addCase(fetchCircleMessages.rejected, (state) => {
        state.loadingCircleMessages = false;
      })
      .addCase(updateCircle.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCircle.fulfilled, (state, action) => {
        if (state.circleData) {
          state.circleData = { ...state.circleData, ...action.payload };
        }
        state.loading = false;
      })
      .addCase(updateCircle.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to update circle details';
      })
      .addCase(editUserCount.pending, (state) => {
        state.error = null;
      })
      .addCase(editUserCount.fulfilled, (state, action) => {
        if (state.circleData) {
          state.circleData.member_count_max = action.payload.member_count_max;
        }
        state.loading = false;
      })
      .addCase(editUserCount.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to edit user count';
      })
      .addCase(fetchMessagesForMember.pending, (state) => {
        state.loadingMemberMessages = true;
      })
      .addCase(fetchMessagesForMember.fulfilled, (state, action) => {
        state.memberMessages = action.payload;
        state.loadingMemberMessages = false;
      })
      .addCase(fetchMessagesForMember.rejected, (state) => {
        state.loadingMemberMessages = false;
      });
  },
});

export const { clearCircleDetail, addVideoMessage, addReplyMessage } =
  circleDetailSlice.actions;

export const selectCircleDetailsData = (state: RootState) =>
  state.circleDetail.circleData;
export const selectAuctionDetailsData = (state: RootState) =>
  state.circleDetail.auctionData;
export const selectCircleDetailLoading = (state: RootState) =>
  state.circleDetail.loading;
export const selectCircleDetailError = (state: RootState) =>
  state.circleDetail.error;

export default circleDetailSlice.reducer;
